import { Component, OnInit, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  config,
  MemberDetails,
  MemberQuery,
  BalanceQuery,
  BalanceService,
  ProviderName,
  MemberCard,
  MemberCardQuery,
  KeyValuePair,
  Cart,
  CartQuery,
  Card,
  CardQuery,
  AuthService,
  CardService,
} from '@fgb/core';
import { Observable, Subject } from 'rxjs';
import { filter, map, takeUntil, tap } from 'rxjs/operators';
import { Populations } from 'src/app/shared/utilities/populations';
import { ProductCode } from 'src/app/shared/utilities/product-code';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { animate, state, style, transition, trigger } from '@angular/animations';

const LCID_LANGUAGE_MAP: Record<string, string> = {
  '1033': 'US',
  '1036': 'FR',
  '1032': 'GR',
  '1037': 'HE',
  '2057': 'EN',
  '4105': 'EN-CA',
  '3084': 'FR-CA',
};

@Component({
  selector: 'fgb-nav-bar',
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
  animations: [
    trigger('rightNavAnimation', [
      state('open', style({ transform: 'translateX(0)', opacity: 1 })),
      state('close', style({ transform: 'translateX(100%)', opacity: 0.2 })),
      transition('open <=> close', [animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)')]),
    ]),
    trigger('rightMenuOverlayAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('300ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 0 }))]),
    ]),
  ],
})
export class NavBarComponent implements OnInit, OnDestroy {
  isNavOpen = false;
  isHelpOpen = false;
  isCollapsed = false;
  currentLanguage = '';
  selectedMember = '';
  readonly populations = Populations;
  readonly productCode = ProductCode;
  memberDetails$!: Observable<MemberDetails | undefined>;
  virtualEcash$!: Observable<number | undefined>;
  loyaltyPointsCard$!: Observable<number | undefined>;
  card$!: Observable<MemberCard | undefined>;
  name$!: Observable<string>;
  cards$!: Observable<Card[]>;
  totalItems$!: Observable<number>;
  basketPointsTotal$!: Observable<number | undefined>;
  items$!: Observable<Cart[] | undefined>;

  private readonly _destroyed$ = new Subject<void>();

  constructor(
    private translate: TranslateService,
    public router: Router,
    public cardService: CardService,
    public memberQuery: MemberQuery,
    private authService: AuthService,
    private balanceQuery: BalanceQuery,
    private balanceService: BalanceService,
    private memberCardQuery: MemberCardQuery,
    private cardQuery: CardQuery,
    private cartQuery: CartQuery
  ) {}

  ngOnInit() {
    this.loyaltyPointsCard$ = this.balanceQuery.selectPurse(config.purseConfig.virtualLoyalty);
    this.balanceService.fetchBalance(config.purseConfig.virtualEcash).subscribe();
    this.virtualEcash$ = this.balanceQuery.selectPurse(config.purseConfig.virtualEcash);
    this.memberDetails$ = this.memberQuery.selectMemberDetails();
    this.card$ = this.memberCardQuery.selectObservedMemberCard();

    this.initMarketplaceCart();
    this.initAccountSwitch();
    this.initLcid();
    this.initRouterListener();
  }

  ngOnDestroy(): void {
    this._destroyed$.next();
    this._destroyed$.complete();
  }

  private initRouterListener(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this._destroyed$)
      )
      .subscribe(() => {
        this.isNavOpen = false;
      });
  }

  private initAccountSwitch(): void {
    this.name$ = this.memberQuery.selectMemberDetails().pipe(map((m) => (m ? `${m.FirstName} ${m.Surname}` : '')));

    this.selectedMember = this.memberQuery.getUserContext().memberId;

    this.cards$ = this.cardQuery
      .selectCards()
      .pipe(
        map((cards: Card[]) =>
          [...cards].sort(
            (a, b) =>
              parseInt(a.Area) - parseInt(b.Area) || parseInt(a.Row) - parseInt(b.Row) || parseInt(a.Seat) - parseInt(b.Seat)
          )
        )
      );
  }

  private initLcid(): void {
    this.memberQuery.userContext$.pipe(takeUntil(this._destroyed$)).subscribe((user) => {
      if (user?.lcid) {
        this.currentLanguage = LCID_LANGUAGE_MAP[user.lcid] ?? '';
      }
    });
  }

  private initMarketplaceCart(): void {
    this.totalItems$ = this.cartQuery.updateTotalItems();
    this.basketPointsTotal$ = this.cartQuery.updateBasketPointsTotal();
    this.items$ = this.cartQuery.selectAllMarketplaceCartData();
  }

  toggleSideNav(): void {
    this.isNavOpen = !this.isNavOpen;
  }

  closeDropDown(dropdown: NgbDropdown): void {
    dropdown.close();
  }

  useLanguage(language: string): void {
    this.translate.use(language);
  }

  logout(): void {
    this.authService
      .logout()
      .pipe(
        tap(() => this.router.navigate(['/login'])),
        takeUntil(this._destroyed$)
      )
      .subscribe();
  }

  selectCard(): void {
    this.router.navigate(['/login']);
  }

  loginWithCard(memberId: string): void {
    this.authService.switchUser(memberId).pipe(takeUntil(this._destroyed$)).subscribe();
  }

  loginThirdParty(): void {
    const options: KeyValuePair[] = [{ key: 'Url', value: 'https://sdp.sportsbingo.ballparkapps.com/?bingoUser={1}' }];
    this.authService.loginThirdParty(ProviderName.SidelineSports, 1, '', true, options);
  }
}
